import React, { useEffect, useRef, useState } from 'react';
import Layout from '../../../components/layout';
import Header from '../../../components/header';
import { Controller, useForm, SubmitHandler } from 'react-hook-form';
import Input from "../../../components/elements/form/input";
import Icon from '../../../components/icons';
import Button from '../../../components/elements/button';
import { useSagesApi } from '../../../store/api/hooks/sageApiHook';
import toast from 'react-hot-toast';
import { useAppLoader } from '../../../_common/pageLoader/appLoaderHook';
import { useDialog } from '../../../store/api/hooks/dialogHook';

type ConectorsFormValueType = {
    companyID?: string;
    senderID?: string;
    senderPassword?: string;
    userID?: string;
    password?: string;
};

const SageConnector: React.FC = () => {
    const sageApi: any = useSagesApi();
    const { control, handleSubmit, setValue, reset, formState: { errors }, watch, getValues, trigger } = useForm<ConectorsFormValueType>
        ({
            defaultValues: {}
        });
    const [showsenderPassword, setShowsenderPassword] = useState(false);
    const [showuserPassword, setShowuserPassword] = useState(false);
    const { showLoader, hideLoader } = useAppLoader();
    const { confirm } = useDialog();
    const [config, setConfig] = useState<ConectorsFormValueType | null>(null);


    const formReset = () => {
        reset({
            companyID: '',
            senderID: '',
            senderPassword: '',
            userID: '',
            password: '',
        });
        setShowsenderPassword(false);
        setShowuserPassword(false);
    };

    const onSubmit: SubmitHandler<ConectorsFormValueType> = formData => {
        showLoader();
        const payload = {
            companyID: formData.companyID,
            senderID: formData.senderID,
            senderPassword: formData.senderPassword,
            userID: formData.userID,
            password: formData.password,
        };

        confirm({
            type: 'warn',
            title: 'Save Sage Connection',
            message: 'Do you want to save the connection?',
            onConfirm: () => {
                sageApi.saveConnection(payload, (resp: any) => {
                    if (resp.successful) {
                        hideLoader();
                        toast.success(resp?.message);
                        formReset();
                        getSageConfig();
                    } else {
                        hideLoader();
                        toast.error(resp.message || "Connection Failed");
                    }
                }, (error: any) => {
                    hideLoader();
                    toast.error(error);
                });
            },
            onCancel: () => {
                hideLoader();
            }
        });


    };


    const callTestConnection = async () => {
        const isValid = await trigger();
        if (!isValid) {
            return;
        }
        showLoader();
        const formData = getValues();
        const payload = {
            companyID: formData.companyID,
            senderID: formData.senderID,
            senderPassword: formData.senderPassword,
            userID: formData.userID,
            password: formData.password,
        };
        sageApi.checkTestConnection(
            payload,
            (resp: any) => {
                console.log("response =>", resp);
                if (resp.successful) {
                    hideLoader();
                    toast.success(resp?.message);
                } else {
                    hideLoader();
                    toast.error(resp.message || "Connection Failed");
                }
            },
            (error: any) => {
                console.log("error =>", error);
                hideLoader();
                toast.error(error);
            }
        );
    };


    const getSageConfig = () => {
        const payload: any = {};
        sageApi.getSageConfig(
            payload,
            (resp: any) => {
                console.log("response =>", resp);
                const data = resp;

                setConfig(data);

                setValue("companyID", data.companyID || '');
                setValue("senderID", data.senderID || '');
                setValue("senderPassword", data.senderPassword || '');
                setValue("userID", data.userID || '');
                setValue("password", data.password || '');
            },
            (error: any) => {
                console.log('error =>', error);
                toast.error(error);
            }
        );
    };

    useEffect(() => {
        getSageConfig();
    }, []);

    return (
        <Layout>
            <Header title="Sage Connector" back="/settings" />
            <div className="flex justify-center items-center min-h-[calc(100vh-100px)] bg-gray-100 px-4 py-8">
                <div className="bg-white shadow-lg rounded-xl p-8 w-full max-w-lg">
                    <h2 className="text-xl font-semibold text-gray-800 mb-6 text-center">
                        Provide Following details to connect with your Sage Intacct
                    </h2>

                    <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
                        <div>
                            <Controller
                                name="companyID"
                                control={control}
                                rules={{ required: "Company ID Required" }}
                                render={({ field: { value, onChange } }) => (
                                    <Input
                                        label="Sage Company ID"
                                        type="text"
                                        value={value}
                                        onChange={onChange}
                                        required
                                        error={errors.companyID?.message}
                                    />
                                )}
                            />
                        </div>
                        <div>
                            <Controller
                                name="senderID"
                                control={control}
                                rules={{ required: "Sender ID Required" }}
                                render={({ field: { value, onChange } }) => (
                                    <Input
                                        label="Sender ID"
                                        type="text"
                                        value={value}
                                        onChange={onChange}
                                        required
                                        error={errors.senderID?.message}
                                    />
                                )}
                            />
                        </div>
                        <div className="relative w-full">
                            <Controller
                                name="senderPassword"
                                control={control}
                                rules={{ required: "Sender Password Is Required" }}
                                render={({ field: { value, onChange } }) => (
                                    <Input
                                        label="Sender Password"
                                        type={showsenderPassword ? 'text' : 'text'} // keep as text to show asterisks
                                        value={
                                            showsenderPassword
                                                ? watch("senderPassword") || ''
                                                : '*'.repeat((watch("senderPassword") || '').length)
                                        }
                                        onChange={(e) => {
                                            const realValue = e.target.value;
                                            setValue("senderPassword", realValue);
                                        }}
                                        required
                                        error={errors.senderPassword?.message}
                                    />


                                )}
                            />
                            <div
                                onClick={() => setShowsenderPassword(prev => !prev)}
                                className="absolute top-1/2 right-2 translate-y-[10%] cursor-pointer text-gray-500 select-none"
                            ><Icon
                                name={showsenderPassword ? "eye-closed" : "eye"} size={1.2}></Icon>

                            </div>
                        </div>

                        <div>
                            <Controller
                                name="userID"
                                control={control}
                                rules={{ required: "Sage User ID Is Required" }}
                                render={({ field: { value, onChange } }) => (
                                    <Input
                                        label="Sage User ID"
                                        type="text"
                                        value={value}
                                        onChange={onChange}
                                        required
                                        error={errors.userID?.message}
                                    />
                                )}
                            />

                        </div>
                        <div className="relative w-full">
                            <Controller
                                name="password"
                                control={control}
                                rules={{ required: "Sage Password Is Required" }}
                                render={({ field: { value, onChange } }) => (
                                    <Input
                                        label="Sage Password"
                                        type={showuserPassword ? 'text' : 'text'}
                                        value={
                                            showuserPassword
                                                ? watch("password") || ''
                                                : '*'.repeat((watch("password") || '').length)
                                        }
                                        onChange={(e) => {
                                            const realValue = e.target.value;
                                            setValue("password", realValue);
                                        }}
                                        required
                                        error={errors.password?.message}
                                    />


                                )}
                            />
                            <div
                                onClick={() => setShowuserPassword(prev => !prev)}
                                className="absolute top-1/2 right-2 translate-y-[10%] cursor-pointer text-gray-500 select-none"
                            ><Icon
                                name={showuserPassword ? "eye-closed" : "eye"} size={1.2}></Icon>

                            </div>
                        </div>


                        <div className="flex justify-between pt-4">
                            <Button type="button" theme='dark' onClick={() => { callTestConnection(); }}>
                                Test Connection
                            </Button>
                            <Button type="button" theme='secondary' onClick={() => { formReset(); }}>
                                Reset
                            </Button>
                            <Button type="submit">
                                Save
                            </Button>
                        </div>

                    </form>
                </div>
            </div>
        </Layout>
    );
};

export default SageConnector;

